import * as React from "react";
import { ReactFlow, Background, BackgroundVariant, type Node, type Edge } from "@xyflow/react";
import "@xyflow/react/dist/style.css";
import nodeTypes from "./nodeTypes";
import DataEdge from "./edgeTypes/DataEdge";
import ExecEdge from "./edgeTypes/ExecEdge";
import "@flows/canvas/colors.css";
import type { StoryMeta } from "@features/devtools/registry/types";
import { inferTypes, applyInferredTypesToNodes } from "@features/flows/lib/type-infer";
import type { NodeTemplate } from "@features/flows/palette/registry";
import { arrayOf, T_STRING } from "@features/flows/utils/types";

type Props = {
  /** Element type for the source array */
  elementType?: "string" | "number" | "boolean";
  /** Apply inference pass to update output pin types */
  applyInference?: boolean;
};

// Minimal template map with a single node that links elementOf(in:items) -> out:value
function buildTemplates(): Map<string, NodeTemplate> {
  const map = new Map<string, NodeTemplate>();
  map.set("core.array.at", {
    key: "core.array.at",
    title: "Array • At",
    category: "Array",
    ports: {
      exec: { in: [{ id: "in", type: "any" }], out: [{ id: "next", type: "any" }] },
      data: {
        in: [{ id: "items", type: "array", typeExpr: { tag: "array", elem: { tag: "any" } } }, { id: "index", type: "number", typeExpr: { tag: "coarse", name: "number" } }],
        out: [{ id: "value", type: "any", typeExpr: { tag: "any" } }],
      },
    },
    attrsSchema: undefined,
    inputSchema: undefined,
    outputSchema: undefined,
    pkg: "core",
    typeLinks: [
      { from: { io: "in", id: "items", op: "element" }, to: { io: "out", id: "value" } },
    ],
  } as any);
  return map;
}

function TypeInferencePreview({ elementType = "string", applyInference = true }: Props) {
  const templates = React.useMemo(() => buildTemplates(), []);
  // Build a tiny graph: Source (array<T>) -> ArrayAt (items)
  const srcElem = elementType;
  const srcTypeExpr = srcElem === "string" ? arrayOf(T_STRING) : srcElem === "number" ? { tag: "array", elem: { tag: "coarse", name: "number" } } as any : { tag: "array", elem: { tag: "coarse", name: "boolean" } } as any;
  const baseNodes: Node[] = [
    {
      id: "src",
      type: "default",
      position: { x: 80, y: 120 },
      data: {
        label: `Source (array<${srcElem}>)`,
        pins: {
          exec: { in: [{ id: "in" }], out: [{ id: "next" }] },
          data: { in: [], out: [{ id: "items", type: "array", typeExpr: srcTypeExpr }] },
        },
      },
    },
    {
      id: "at",
      type: "default",
      position: { x: 420, y: 120 },
      data: {
        label: "Array • At",
        templateKey: "core.array.at",
        pins: {
          exec: { in: [{ id: "in" }], out: [{ id: "next" }] },
          data: {
            in: [{ id: "items", type: "array" }, { id: "index", type: "number" }],
            out: [{ id: "value", type: "any" }],
          },
        },
      },
    },
  ];
  const baseEdges: Edge[] = [
    {
      id: "e-items",
      type: "data",
      source: "src",
      target: "at",
      sourceHandle: "data:out:items",
      targetHandle: "data:in:items",
      data: { channel: "data", rawSourceHandle: "items", rawTargetHandle: "items" },
    } as any,
  ];

  const edgeTypes = React.useMemo(() => ({ data: DataEdge, exec: ExecEdge }), []);
  const [nodes, setNodes] = React.useState<Node[]>(baseNodes);
  const [edges, setEdges] = React.useState<Edge[]>(baseEdges);

  React.useEffect(() => {
    if (!applyInference) { setNodes(baseNodes); setEdges(baseEdges); return; }
    try {
      const inferred = inferTypes(baseNodes as any, baseEdges as any, templates);
      const nextNodes = applyInferredTypesToNodes(baseNodes as any, inferred) as any as Node[];
      setNodes(nextNodes);
      setEdges(baseEdges);
    } catch {
      setNodes(baseNodes);
      setEdges(baseEdges);
    }
  }, [applyInference, elementType, templates]);

  return (
    <div className="h-[560px] border rounded">
      <ReactFlow<Node, Edge>
        nodes={nodes}
        edges={edges}
        nodeTypes={nodeTypes}
        edgeTypes={edgeTypes}
        fitView
        panOnScroll
        panOnDrag
        zoomOnScroll
      >
        <Background variant={BackgroundVariant.Dots} gap={24} size={1} />
      </ReactFlow>
    </div>
  );
}

const meta: StoryMeta<Props> = {
  id: "flows/TypeInference",
  title: "Type Inference",
  group: "Flows/Typing",
  component: TypeInferencePreview as any,
  description: "Demonstrates Array<T> ⇒ T inference via elementOf link.",
  defaultProps: { elementType: "string", applyInference: true },
  controls: {
    elementType: { type: "select", label: "Element Type", options: ["string", "number", "boolean"] },
    applyInference: { type: "boolean", label: "Apply Inference" },
  },
  variants: [
    { name: "Before (no inference)", props: { applyInference: false } },
    { name: "After (string)", props: { elementType: "string", applyInference: true } },
    { name: "After (number)", props: { elementType: "number", applyInference: true } },
  ],
};

export default meta;

